import { URL_SLUG_TO_RACE_HASH_MAP } from "@/src/elections/business-layer/constants"
import { Race } from "@/src/elections/business-layer/types"
import { ElectionSources2023 } from "@/src/elections/data-layer/types"
import {
  CompaniesFilter,
  CompanySorting,
  CompanyTransactionHistoryFilter,
  CountryOfOperationFilter,
  DateRangeFilter,
  DealFiltering,
  DealSorting,
  FinancialNode,
  FinancialsFilter,
  FloatRangeFilter,
  GenericOptionsSchema,
  IntRangeFilter,
  InvestmentArmMetricsFilter,
  InvestmentArmSorting,
  LimitedPartnerFilter,
  PageInfo
} from "@/src/graphql/types"
import {
  CompaniesDocRes,
  CountriesDocRes,
  InvestmentStrategiesDocRes,
  InvestorsDocRes,
  LayoutPropsV3,
  LeadInvestorDocRes,
  LoggedInUser,
  TagClassificationTypes,
  TransactionsDocRes,
  VCFundingRoundDocRes
} from "@/src/sb/business-layer/types"
import { SortingState } from "@tanstack/react-table"
import { GetServerSidePropsResult } from "next"
import React from "react"

import { SEOProps } from "../sb/components/SEO/settings"

type SEOKey = {
  seo: Partial<SEOProps>
}

export type StearsServerSideProps<T> = GetServerSidePropsResult<T & SEOKey>
export type OperatingSystems = "android" | "iOS" | "unknown"

export type ElectionTime = {
  year: string
  isGeneralElectionYear: boolean
  races: Record<string, Array<string>>
}
export type RawElectionTime = { year: string; race: string; state: string }
export type ElectionPageParams = Omit<RawElectionTime, "race"> & {
  raceUrlSlug: keyof typeof URL_SLUG_TO_RACE_HASH_MAP
}
export type PartyDominanceApiParams = {
  state?: string
  source?: ElectionSources2023
} & Omit<RawElectionTime, "state">
export type PartyDominanceTableRow = {
  name: string
  key: string
  href: string
  winner: string
} & Record<string, string | number | undefined>

export type PartyDominancePartyData = {
  name: string
  code: string
  votes: number
  totalVotes: number
  votePercentage: number | null
  color: string | null
  race: Race
  /**
   * records the number of candidate/members this party supports for this race
   */
  logoURL?: string | null
  seatsWon: number
}

export type LiveBlogResponse = {
  html: string
  schema: Record<string, any>
}

export type FormState = "idle" | "pending" | "error" | "success"

export type AnalyticsProfileUpdateProps = {
  type: "new_user" | "existing_user"
  member_status: LoggedInUser["userType"]
  state: "failure" | "success"
}

export type City = {
  name: string
  state: string
  country: string
}

export type Column = {
  key: string
  label: string
}

export type FMDQTableProps = {
  columns: Column[]
  rows: { [key: string]: any }[]
  loading?: boolean
}

export type OptionType = {
  value: string
  label: string
}
export type CheckboxProps = {
  id: string | undefined
  checked?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  label?: string
  name?: string
}

export type CardProps = {
  onClose?: () => void
  className?: string
  children?: React.ReactNode
  onApply?: () => void
  showCard: boolean
  widthClassName?: string
}

export type ColumnSelectorProps = {
  columns: Column[]
  checkboxState: Record<string, boolean>
  handleCardDisplay: () => void
  handleCheckboxChange: (key: string, checked: boolean) => void
  handleApplyColumns: () => void
  handleCloseColumns: () => void
  showCard: boolean
  handleResetColumns: () => void
}

export type DynamicSelectProps = {
  options?: OptionType[]
  value?: string
  onChange?: (selectedValue: string) => void
  placeholder?: any
  onClick?: () => void
  theme?: "default" | "custom"
}

export type DataRow = {
  [key: string]: string | number | null
}

export type ExportTableDataProps = {
  data: DataRow[]
}

export type CustomDateRangeProps = {
  showCard: boolean
  handleCardDisplay: () => void
  handleApplyDateRange: (
    option: string | null,
    startDate: Date | null,
    endDate: Date | null
  ) => void
  handleCloseDateRange: () => void
  handleResetDate: () => void
}

export type GetFullFMDQLabel = (
  key: string,
  label: string,
  columns: Column[]
) => string

export type AnnouncementDate = {
  start: Date | null
  end: Date | null
  operationName?: string
  predefinedOperation?: string
  operation?: string
}

export type FinancialsFilterState = {
  selectedOption?: string | null
  isPeriodOpen?: boolean
  selectedStartYear?: string | null
  selectedEndYear?: string | null
  selectedCurrency?: {
    selectedOption: string | null | undefined
  }
  filterBy?: { reportingDate: DateRangeFilter } | null
  currencyOptions?: []
  appliedCurrency?: {
    selectedOption: string | null | undefined
  }
  financialsFilterState?: any
  companyName?: string
}

export type FinancialsFilterProps = {
  options?: OptionType[]
  value?: string
  financialsFilter: FinancialsFilterState
  onChange?: (
    key: keyof FinancialsFilterState,
    value: string | boolean | null | undefined
  ) => void
  placeholder?: string
  reportingCurrency?: string
  className?: string
  onApply?: () => void
  isOpen?: boolean
  onCancel?: () => void
  onReset?: () => void
  filterTitle?: string
  disableFilter?: boolean
}

export type MetricsEntry = {
  metrics: string | number
  isGroupHeader: boolean
  [key: string]: string | number | boolean | null
}

export type ExportEntry = Omit<MetricsEntry, "isGroupHeader"> & {
  "Reporting Currency": string
}

export type FinancialsMetrics = {
  label: string
  key: string
  tooltipContent: string
}

export type WithType = { type?: string }

export type ExtendedFoundingYear = IntRangeFilter & {
  operationName?: string
  predefinedOperation?: string
  selectedOption?: string
}

export type ExtendedFinancialFilter = FinancialsFilter &
  WithType & {
    revenue?: FloatRangeFilter & WithType
    ebitda?: FloatRangeFilter & WithType
    operatingExpenses?: FloatRangeFilter & WithType
    percentagePriceToEarningRatio?: FloatRangeFilter & WithType
    evSales?: FloatRangeFilter & WithType
    priceToBookRatio?: FloatRangeFilter & WithType
    assetsUnderManagement?: FloatRangeFilter & WithType
    totalAmountCommitted?: FloatRangeFilter & WithType
    [key: string]: (FloatRangeFilter & WithType) | undefined
  }

export type ExtendedCompanyTransactionHistory =
  CompanyTransactionHistoryFilter &
    WithType & {
      totalAmountRaised?: FloatRangeFilter & WithType
      lastTransactionAmount?: FloatRangeFilter & WithType
      lastPreMoneyValuation?: FloatRangeFilter & WithType
      lastPostMoneyValuation?: FloatRangeFilter & WithType
      latestTransactionType?: FloatRangeFilter & WithType & { slug?: string }
      latestFinancingType?: FloatRangeFilter & WithType & { slug?: string }
      currentInvestors?: FloatRangeFilter & WithType & { slug?: string }
    }

export type ExtendedCompanyFiltering = CompaniesFilter & {
  company?: CompaniesFilter
  foundingYear: ExtendedFoundingYear
  investor?: CompaniesFilter
  financial: ExtendedFinancialFilter
  transactionType?: CompaniesFilter
  financingType?: CompaniesFilter
  deal?: ExtendedCompanyTransactionHistory
  appliedFields?: string[]
  transactionHistory?: ExtendedCompanyTransactionHistory
}

export type ExtendedLimitedPartnerFiltering = LimitedPartnerFilter & {
  company?: CompaniesFilter
  foundingYear: ExtendedFoundingYear
  financial: ExtendedFinancialFilter
  countryOfOperation: CountryOfOperationFilter
  deal?: ExtendedCompanyTransactionHistory
  appliedFields?: string[]
  fund: ExtendedFinancialFilter
  limitedPartnerType: GenericOptionsSchema
  selectedFundCommitmentDate?: string
  selectedAfricanFundCommitmentDate?: string

  /*Todo: Replace these types with actual gpl types when made available */
  investmentInstrument?: {
    slug: string[]
  }

  investmentStrategy?: {
    slug: string[]
  }
  investmentFundManager?: {
    slug: string[]
  }

  commitmentMetrics?: InvestmentArmMetricsFilter & {
    lastAfricanFundCommitmentDate?: AnnouncementDate
    lastFundCommitmentDate?: AnnouncementDate
    noOfCommitmentsInAfricanFund?: FloatRangeFilter & WithType
  }

  fundName?: string[]
}

export type CompanyScreenerType = {
  layoutProps: LayoutPropsV3
  screenerData: ExtendedCompanyFiltering
  setScreenerData: React.Dispatch<
    React.SetStateAction<ExtendedCompanyFiltering>
  >
}

export type LimitedPartnerScreenerType = {
  layoutProps: LayoutPropsV3
  screenerData: ExtendedLimitedPartnerFiltering
  setScreenerData: React.Dispatch<
    React.SetStateAction<ExtendedLimitedPartnerFiltering>
  >
}

export type ListData = {
  limit: number
  pageNumber?: number
  sorting?: SortingState
  dealsVar?: PageInfo & {
    orderBy?: DealSorting | CompanySorting | InvestmentArmSorting
    filterBy?: DealFiltering | CompaniesFilter | LimitedPartnerFilter
    limit?: number
  }
  currentPageNumber?: number
  loading?: boolean
  data?: any
  isExportModalOpen?: boolean
  columnSelectionState?: Record<string, boolean>
  companyColumnSelectionState?: Record<string, boolean>
  appliedColumnSelectionState?: Record<string, boolean>
  showFilters?: boolean
}

export type ListProps = {
  layoutProps?: LayoutPropsV3
  listData: ListData
  setListData: React.Dispatch<React.SetStateAction<ListData>>
  sorting: SortingState
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  countries?: CountriesDocRes
  vcFundingRounds?: VCFundingRoundDocRes
  financingInstruments?: any
  transactionTypes?: TransactionsDocRes
  investmentStrategies?: InvestmentStrategiesDocRes
  investors?: InvestorsDocRes
  leadInvestors?: LeadInvestorDocRes
  IndustryClassification?: TagClassificationTypes
  targets?: CompaniesDocRes
  showColumnList?: boolean
  disableSorting?: boolean
}

export type TransactionsPageProps = {
  layoutProps?: LayoutPropsV3
}

export type TransactionProfilePageProps = {
  layoutProps?: LayoutPropsV3
}

export type GlobalSearchState = {
  mounted?: boolean
  searchQuery?: string
  debounceQuery?: string
  loading?: boolean
  result?: {
    activeSearch?: string
    activeParent?: string
    activeChild?: string
    activeId?: number | string
  }
}

type FilterItem = {
  attribute: string
  operator: number
  value: string
}

type Target = {
  indexType: string
  filterItems: FilterItem[]
}

export type SearchFilter = {
  id: number
  name: string
  active: boolean
  order: number
  targets: Target[]
  children: SearchFilter[]
}

export type Subsector = {
  name: string
}

type Country = {
  name: string
}

export type SearchResultsType = {
  objectID: string
  name: string
  title?: string
  tags?: { name: string }[]
  subsectors?: Subsector[]
  countriesOfOperation?: Country[]
  entityType?: string
  totalDealAmount?: number
  fund?: string
  currency?: string
  announcementDate?: string
  objectId?: string | number
  slug?: string
  bucketSlug?: string
  groupSlug?: string
  topics?: { name: string }[]
  author?: string
  metaTag?: { name: string }
  companyName?: string
  companySlug?: string
  date?: string
  classification?: string
  countryRiskName?: string
  countryRiskSlug?: string
  tabSlug?: string
  serialNo?: string
  investorTypes?: { name: string }[]
  geoPreferences?: { name: string }[]
}

export enum EntityType {
  REPORT = "report",
  REPORTS = "reports",
  DATASET = "dataset",
  DATASETS = "datasets",
  COUNTRY_PROFILE = "country_profile",
  TRANSACTIONS = "transactions",
  TRANSACTION = "transaction",
  COMPANY = "company",
  COMPANIES = "companies",
  LIMITED_PARTNER = "limited_partner"
}

export type INSTITUTION_PROPS = {
  loading: boolean
  showFilters?: boolean
  companyDocumentType?: any
  companyData?: any
  handleApply?: () => void
  handleReset?: () => void
  incomeStatementData: FinancialsFilterState
  setIncomeStatementData: React.Dispatch<
    React.SetStateAction<FinancialsFilterState>
  >
  parentSelectedCurrency?: string
}

export type ExtendedFinancialNode = FinancialNode & {
  isGroupHeader?: boolean
  metrics: string
}

export type FinancialsIncomeValue = {
  incomeStatementMetricsMapping: FinancialsMetrics[]
}

export type ChartType = {
  id: number
  label: string
  chartIframe: string
}

export type ScreenerOptionType =
  | "recent_transactions"
  | "companies"
  | "limited_partners"

export type ScreenEntityType = "companies" | "transactions" | "limited_partners"

export type RecentSearchItem = {
  name: string
}

export type FinancialFiltersProp = {
  showFilters?: boolean
  appliedCurrency?: string
  parentSelectedCurrency?: string
}

export type COMPANY_OVERVIEW_SECTION =
  | "General Information"
  | "Investments"
  | "Research"

export type BreakdownItem = {
  fundCount: string
  percentageDistribution: number
  sector: { name: string; slug: string }
  region?: { name: string; slug: string }
  [key: string]: any
}

export type FormattedBreakdownItem = {
  category: string
  investmentCount: number
  distribution: number
}

export type GPBreakdownItem = {
  fundCount: string
  percentageDistribution: number
  sector: { name: string; slug: string }
  region?: { name: string; slug: string }
  [key: string]: any
}

export type GPFormattedBreakdownItem = {
  category: string
  fundCount: number
  distribution: number
}

export type CategoryKey = "sector" | "region"
